/**
 * FK: アンダーシャツ・アームスリーブのプリント加工費 — 決済前の照合（2026-10-19）
 *
 * カートで「ご購入手続きへ」を押したとき、プリント内容ごとに「本体の合計枚数」と「その種類の加工費の合計数」を比べ、
 * 合わないときだけ確認を出す（全体の過不足 → 対象の一覧）。
 * ボタンは〔合わせて進む〕〔カートに戻って直す〕の 2 つ。〔このまま進む〕は〔合わせて進む〕が失敗したときだけ出す（2026-10-19 人間）。
 * 🔴 お客様を止めない＝照合そのものが失敗したら何も出さずに進める。自動では直さない（直すのはお客様が押したときだけ）。
 * 管理画面「FK プリント加工費」で OFF にすると何もしない。
 * ⚠️ プリントの項目があるのに「プリント内容」を持たない本体の行（2026-10-20 より前に入ったもの）があれば、照合しない。
 *
 * 📌 カートへ入れる処理（加工費 → 本体・本体に「プリント内容」を付ける）は snippets/add-to-cart-button.liquid が持つ。
 *    🔴 下の対応表は、そちらの PRINT_VARIANTS と PRINT_LABELS と同じ値でなければならない（片方だけ直さない）。
 */

/** 選択肢 → 加工費の変種 ID と「プリント内容」（snippets/add-to-cart-button.liquid の PRINT_VARIANTS / PRINT_LABELS と同じ値） */
const PRINT_OPTIONS = {
  number: { variantId: 44755821035562, label: '背番号のみ' },
  alpha: { variantId: 44755821068330, label: '名入れのみ' },
  both_num_first: { variantId: 44755821101098, label: '背番号＋名入れ' },
  both_alpha_first: { variantId: 44758564208682, label: '名入れ＋背番号' },
  silhouette: { variantId: 44755821133866, label: 'シルエットのみ' },
  silh_number: { variantId: 44758564438058, label: 'シルエット＋背番号' },
  silh_alpha: { variantId: 44758564667434, label: 'シルエット＋名入れ' },
  armsleeve_number: { variantId: 44758565683242, label: '背番号（アームスリーブ）' },
};

const LABEL_BY_VARIANT = Object.fromEntries(Object.values(PRINT_OPTIONS).map((o) => [o.variantId, o.label]));
const VARIANT_BY_LABEL = Object.fromEntries(Object.values(PRINT_OPTIONS).map((o) => [o.label, o.variantId]));

const KIND_KEY = 'プリント内容';
/** 本体の行がプリント付きかを見分ける項目（「プリント内容」が無い旧い行の検出に使う） */
const PRINT_FIELD_KEYS = ['背番号', '名入れ', 'プリント色', 'シルエット用画像'];
/** 加工費の行の注意書き（足すときに付ける・スニペットの KEEP_NOTICE と同じ値） */
const NOTICE_KEY = 'ご注意';
const NOTICE_VALUE = '削除するとプリントされません';

const isFeeLine = (item) => Number(item.variant_id) in LABEL_BY_VARIANT;

async function fetchCart() {
  const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.description || json.message || `${url} ${res.status}`);
  return json;
}

/* ───────────── ② 決済の直前に照合する ───────────── */

/**
 * 照合の結果＝数が合わない「プリント内容」だけ（合っていれば空）。
 * プリントの項目があるのに「プリント内容」を持たない本体の行があれば null（照合しない）。
 */
function findMismatches(items) {
  /** @type {Map<string, {label: string, shirts: any[], shirtQty: number, fees: any[], feeQty: number}>} */
  const groups = new Map();
  const groupOf = (label) => {
    if (!groups.has(label)) groups.set(label, { label, shirts: [], shirtQty: 0, fees: [], feeQty: 0 });
    return groups.get(label);
  };

  for (const item of items) {
    if (isFeeLine(item)) {
      const group = groupOf(LABEL_BY_VARIANT[Number(item.variant_id)]);
      group.fees.push(item);
      group.feeQty += item.quantity;
      continue;
    }
    const label = item.properties?.[KIND_KEY];
    if (!label) {
      if (PRINT_FIELD_KEYS.some((k) => item.properties?.[k])) return null; // 旧い行が混じっている
      continue;
    }
    const group = groupOf(label);
    group.shirts.push(item);
    group.shirtQty += item.quantity;
  }
  return [...groups.values()].filter((g) => g.shirtQty !== g.feeQty);
}

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** 本体の行の項目を、カートの表示と同じ並び（プリント色 → 背番号/名入れ → 画像）で 1 行にする */
function describeShirt(item) {
  const p = item.properties || {};
  const parts = [];
  if (p['プリント色']) parts.push(String(p['プリント色']));
  const middle = p[KIND_KEY] === PRINT_OPTIONS.both_alpha_first.label ? ['名入れ', '背番号'] : ['背番号', '名入れ'];
  for (const k of middle) if (p[k]) parts.push(`${k} ${p[k]}`);
  if (p['シルエット用画像']) parts.push('シルエット画像あり');
  const name = `${item.product_title}${item.variant_title ? `（${item.variant_title}）` : ''} × ${item.quantity}`;
  return `${escapeHtml(name)}<br><small>${parts.map(escapeHtml).join('・')}</small>`;
}

/** 全体の過不足（足りない合計・多い合計）を、先頭に 1 回だけ出す */
function describeSummary(mismatches) {
  let short = 0;
  let over = 0;
  for (const g of mismatches) {
    if (g.feeQty < g.shirtQty) short += g.shirtQty - g.feeQty;
    else over += g.feeQty - g.shirtQty;
  }
  const blocks = [];
  if (short) blocks.push([`⚠️ 足りない加工費が ${short} つあります`, `※このままでは ${short} 枚分はプリントされません`]);
  if (over) blocks.push([`⚠️ 余分な加工費が ${over} つあります`, `※このままでは ${over} 枚分多く支払ってしまいます`]);
  return blocks
    .map(([head, note]) => `<p class="fk-print-check__verdict">${head}</p><p class="fk-print-check__note">${note}</p>`)
    .join('');
}

/** 対象 1 件＝プリント内容・その過不足・該当する商品 */
function describeGroup(g) {
  const diff = g.feeQty - g.shirtQty;
  const tag = diff > 0 ? `${diff} つ多い` : `${-diff} つ不足`;
  const shirts = g.shirts.length ? g.shirts.map(describeShirt).join('<br>') : 'このプリント内容の商品はありません';
  return `
    <li class="fk-print-check__item">
      <p class="fk-print-check__spec">${escapeHtml(g.label)}<span class="fk-print-check__tag">${tag}</span></p>
      <p class="fk-print-check__count">商品 ${g.shirtQty} 枚 ／ 加工費 <span class="fk-print-check__bad">${g.feeQty}</span> つ</p>
      <p>${shirts}</p>
    </li>`;
}

/** 数を合わせる＝多い分は減らし、足りない分はその種類の加工費を足す */
async function fixMismatches(mismatches) {
  const updates = {};
  const adds = [];
  for (const g of mismatches) {
    if (g.feeQty > g.shirtQty) {
      let remaining = g.shirtQty;
      for (const fee of g.fees) {
        const keep = Math.min(fee.quantity, remaining);
        updates[fee.key] = keep;
        remaining -= keep;
      }
    } else {
      const variantId = VARIANT_BY_LABEL[g.label];
      if (!variantId) throw new Error(`加工費が分からないプリント内容: ${g.label}`);
      adds.push({ id: variantId, quantity: g.shirtQty - g.feeQty, properties: { [NOTICE_KEY]: NOTICE_VALUE } });
    }
  }
  if (Object.keys(updates).length) await postJson('/cart/update.js', { updates });
  if (adds.length) await postJson('/cart/add.js', { items: adds });
}

/**
 * カートのフォームを送り直さずに決済へ進む。
 * 🔴 フォームは数量（updates[]）も送るので、数を直したあとに送り直すと元の数へ戻ってしまう。
 *    ⇒ 配送日時などの attributes とメモだけ保存してから決済画面へ移る。
 */
async function goToCheckoutKeepingForm(form) {
  const attributes = {};
  let note;
  for (const [name, value] of new FormData(form).entries()) {
    const m = /^attributes\[(.+)\]$/.exec(name);
    if (m) attributes[m[1]] = String(value);
    else if (name === 'note') note = String(value);
  }
  const body = { attributes };
  if (note !== undefined) body.note = note;
  await postJson('/cart/update.js', body);
  window.location.href = '/checkout';
}

function openDialog(mismatches, { onFix, onContinue }) {
  document.getElementById('fk-print-check')?.remove();
  const dialog = document.createElement('dialog');
  dialog.id = 'fk-print-check';
  dialog.className = 'fk-print-check';
  dialog.innerHTML = `
    <style>
      .fk-print-check { max-width: 30rem; width: calc(100% - 2rem); padding: 1.25rem; border: none; border-radius: 8px; box-shadow: 0 8px 32px rgb(0 0 0 / 0.25); }
      .fk-print-check::backdrop { background: rgb(0 0 0 / 0.4); }
      .fk-print-check h2 { font-size: 1.1em; margin: 0 0 0.75rem; }
      .fk-print-check__verdict { color: #b71c1c; font-weight: bold; margin: 0; }
      .fk-print-check__note { color: #b71c1c; font-size: 0.9em; margin: 0.15rem 0 0.75rem; }
      .fk-print-check__label { font-size: 0.85em; margin: 0.75rem 0 0.25rem; opacity: 0.7; }
      .fk-print-check ul { list-style: none; margin: 0 0 1rem; padding: 0 0.75rem; border: 1px solid #e0e0e0; border-radius: 6px; font-size: 0.9em; }
      .fk-print-check__item { padding: 0.75rem 0; }
      .fk-print-check__item + .fk-print-check__item { border-top: 1px solid #e0e0e0; }
      .fk-print-check__item p { margin: 0 0 0.25rem; }
      .fk-print-check__spec { font-weight: bold; display: flex; justify-content: space-between; gap: 0.5rem; }
      .fk-print-check__tag { color: #b71c1c; white-space: nowrap; }
      .fk-print-check__count { font-size: 0.9em; }
      .fk-print-check__bad { color: #b71c1c; font-weight: bold; }
      .fk-print-check__error { color: #b71c1c; font-size: 0.9em; margin: 0 0 0.75rem; }
      .fk-print-check__actions { display: grid; gap: 0.5rem; }
      .fk-print-check__actions button { padding: 0.75rem; border-radius: 6px; font-size: 0.95em; cursor: pointer; border: 1px solid #999; background: #fff; color: #222; }
      .fk-print-check__actions .fk-print-check__primary { background: #222; color: #fff; border-color: #222; }
      .fk-print-check__actions button[disabled] { opacity: 0.5; cursor: wait; }
    </style>
    <h2>プリントの内容をご確認ください</h2>
    ${describeSummary(mismatches)}
    <p class="fk-print-check__label">対象</p>
    <ul>${mismatches.map(describeGroup).join('')}</ul>
    <p class="fk-print-check__error" hidden></p>
    <div class="fk-print-check__actions">
      <button type="button" class="fk-print-check__primary" data-fk-action="fix">加工費を商品の数に合わせて進む</button>
      <button type="button" data-fk-action="back">カートに戻って直す</button>
      <button type="button" data-fk-action="continue" hidden>このまま進む</button>
    </div>`;
  document.body.append(dialog);

  const buttons = [...dialog.querySelectorAll('button')];
  const error = dialog.querySelector('.fk-print-check__error');
  dialog.addEventListener('close', () => dialog.remove());
  dialog.addEventListener('click', async (e) => {
    const action = e.target instanceof Element ? e.target.closest('[data-fk-action]')?.dataset.fkAction : null;
    if (action === 'back') dialog.close();
    if (action === 'continue') {
      dialog.close();
      onContinue();
    }
    if (action === 'fix') {
      buttons.forEach((b) => (b.disabled = true));
      try {
        await onFix();
      } catch (err) {
        console.error('[fk-print-option]', err);
        error.textContent = 'うまく合わせられませんでした。カートに戻って直すか、このままお進みください。';
        error.hidden = false;
        buttons.forEach((b) => (b.disabled = false));
        // ふだんは出さない。合わせる処理が失敗したときだけ、お客様を止めないために出す
        dialog.querySelector('[data-fk-action="continue"]').hidden = false;
      }
    }
  });
  dialog.showModal();
}

// 配送日時の判定（fk-delivery-date.js）の後に走る＝そちらが止めたらここは呼ばれない
document.addEventListener(
  'submit',
  (event) => {
    if (!window.FK_PRINT_CHECK_ENABLED) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
    const submitter = event.submitter;
    if (submitter && submitter.name !== 'checkout') return;

    // 照合を済ませた（または「このまま進む」を押した）送り直し
    if (form.dataset.fkPrintChecked === 'true') {
      delete form.dataset.fkPrintChecked;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const proceed = () => {
      form.dataset.fkPrintChecked = 'true';
      form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
    };

    fetchCart()
      .then((cart) => {
        const mismatches = findMismatches(cart.items);
        if (!mismatches || !mismatches.length) return proceed(); // null＝旧い行が混じっていて照合しない
        openDialog(mismatches, {
          // 押すたびにカートを読み直し、その時点のずれだけを合わせる（失敗後の押し直しで二重に足さない）
          onFix: async () => {
            const current = findMismatches((await fetchCart()).items);
            if (current && current.length) await fixMismatches(current);
            await goToCheckoutKeepingForm(form);
          },
          onContinue: proceed,
        });
      })
      // 照合そのものが失敗したら、止めずに進める（最後の網は運営あての新規注文メール）
      .catch((error) => {
        console.error('[fk-print-option]', error);
        proceed();
      });
  },
  true
);
